import { useCallback, useEffect, useRef, useState } from 'react'
import type { PDFPageProxy, RenderTask } from 'pdfjs-dist'
import type { Feuillet } from '../lib/types'

/**
 * Affiche une page du plan et permet d'avancer ou de reculer page par page :
 * boutons ‹ ›, flèches du clavier (ou Page préc./suiv.), ou glissement du
 * doigt vers la gauche ou la droite sur téléphone.
 * Les annotations arrivent en phase 2.
 *
 * Les pages viennent d'une « source » : soit le PDF complet, soit un plan
 * découpé dont chaque page est téléchargée seulement quand on l'ouvre.
 *
 * Netteté au zoom : deux couches.
 *  - « fond » : la page entière, dessinée une fois à la taille de l'écran ;
 *  - « détail » : seulement la partie visible, redessinée à pleine
 *    résolution après chaque zoom ou déplacement.
 * Ainsi le plan reste net à tous les niveaux de zoom, sans jamais créer une
 * image géante que le téléphone refuserait (les iPhone plafonnent une image
 * à environ 16 millions de pixels).
 */
export interface SourcePages {
  total: number
  page: (n: number) => Promise<PDFPageProxy>
}

const ZOOM_MAX = 40

export function VisualiseurPdf({
  source,
  nomDocument,
  feuillets,
  page,
  allerA,
  index
}: {
  source: SourcePages
  nomDocument: string
  feuillets: Map<number, Feuillet>
  page: number
  allerA: (page: number) => void
  index: () => void
}) {
  const total = source.total
  const [erreur, setErreur] = useState<string | null>(null)
  const [zoom, setZoom] = useState(1)
  const [saisie, setSaisie] = useState(String(page))
  const [rendu, setRendu] = useState(false)
  const [main, setMain] = useState(false) // vrai pendant qu'on déplace le plan à la souris

  const cadre = useRef<HTMLDivElement>(null)
  const feuille = useRef<HTMLDivElement>(null) // conteneur à la taille zoomée
  const fond = useRef<HTMLCanvasElement>(null)
  const detail = useRef<HTMLCanvasElement>(null)
  const pageCourante = useRef<PDFPageProxy | null>(null)
  const tacheFond = useRef<RenderTask | null>(null)
  const tacheDetail = useRef<RenderTask | null>(null)
  const tailleBase = useRef({ l: 0, h: 0, ajuste: 1 }) // taille à 100 %
  const zoomVisuel = useRef(1)
  const minuterie = useRef<number | undefined>(undefined)
  const minuterieDetail = useRef<number | undefined>(undefined)
  const glisse = useRef<{ x: number; y: number; sl: number; st: number } | null>(null)
  const toucher = useRef<{ x: number; y: number } | null>(null)
  const pince = useRef<{ dist: number; z: number; fx: number; fy: number } | null>(null)

  const precedente = useCallback(() => allerA(Math.max(1, page - 1)), [allerA, page])
  const suivante = useCallback(() => allerA(Math.min(total, page + 1)), [allerA, page, total])

  useEffect(() => setSaisie(String(page)), [page])

  /** Applique une taille d'affichage au conteneur (sans redessiner). */
  const appliquerTaille = useCallback((z: number) => {
    const el = feuille.current
    if (!el || !tailleBase.current.l) return
    el.style.width = `${Math.floor(tailleBase.current.l * z)}px`
    el.style.height = `${Math.floor(tailleBase.current.h * z)}px`
  }, [])

  /** Redessine en pleine résolution la partie visible de la page. */
  const dessinerDetail = useCallback(async () => {
    const p = pageCourante.current
    const c = detail.current
    const el = feuille.current
    const boite = cadre.current
    if (!p || !c || !el || !boite) return
    const z = zoomVisuel.current
    tacheDetail.current?.cancel()
    if (z <= 1.02) {
      c.style.display = 'none'
      return
    }
    const dpr = window.devicePixelRatio || 1
    // Zone visible, en pixels d'écran, relative au coin de la page.
    const x0 = Math.max(0, boite.scrollLeft - el.offsetLeft)
    const y0 = Math.max(0, boite.scrollTop - el.offsetTop)
    const l = Math.min(boite.clientWidth, el.clientWidth - x0)
    const h = Math.min(boite.clientHeight, el.clientHeight - y0)
    if (l <= 0 || h <= 0) return
    const vue = p.getViewport({ scale: tailleBase.current.ajuste * z * dpr })
    const hors = document.createElement('canvas')
    hors.width = Math.ceil(l * dpr)
    hors.height = Math.ceil(h * dpr)
    const ctx = hors.getContext('2d')
    if (!ctx) return
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, hors.width, hors.height)
    const t = p.render({
      canvasContext: ctx,
      viewport: vue,
      transform: [1, 0, 0, 1, -x0 * dpr, -y0 * dpr]
    })
    tacheDetail.current = t
    try {
      await t.promise
    } catch {
      return // annulé : un nouveau zoom ou déplacement est en cours
    }
    // Le zoom a pu changer pendant le dessin : on ne pose pas une image périmée.
    if (zoomVisuel.current !== z) return
    c.width = hors.width
    c.height = hors.height
    c.style.left = `${x0}px`
    c.style.top = `${y0}px`
    c.style.width = `${l}px`
    c.style.height = `${h}px`
    c.getContext('2d')?.drawImage(hors, 0, 0)
    c.style.display = 'block'
  }, [])

  const planifierDetail = useCallback(
    (delai = 140) => {
      window.clearTimeout(minuterieDetail.current)
      minuterieDetail.current = window.setTimeout(() => void dessinerDetail(), delai)
    },
    [dessinerDetail]
  )

  // Ouverture d'une page : on dessine le fond (page entière, à l'échelle de l'écran).
  useEffect(() => {
    let actif = true
    setZoom(1)
    zoomVisuel.current = 1
    if (detail.current) detail.current.style.display = 'none'
    ;(async () => {
      let p: PDFPageProxy
      try {
        p = await source.page(page)
      } catch (e) {
        if (actif) setErreur(e instanceof Error ? e.message : String(e))
        return
      }
      if (!actif || !cadre.current || !fond.current) return
      setErreur(null)
      const base = p.getViewport({ scale: 1 })
      const ajuste = Math.min(
        (cadre.current.clientWidth - 16) / base.width,
        (cadre.current.clientHeight - 16) / base.height
      )
      const dpr = window.devicePixelRatio || 1
      // Un peu plus fin que l'écran (×1,5) pour les premiers niveaux de zoom,
      // plafonné pour rester léger en mémoire.
      const echelle = Math.min(ajuste * dpr * 1.5, 4096 / Math.max(base.width, base.height))
      const vue = p.getViewport({ scale: echelle })
      tacheFond.current?.cancel()
      const hors = document.createElement('canvas')
      hors.width = Math.floor(vue.width)
      hors.height = Math.floor(vue.height)
      const ctx = hors.getContext('2d')
      if (!ctx) return
      const t = p.render({ canvasContext: ctx, viewport: vue })
      tacheFond.current = t
      try {
        await t.promise
      } catch {
        return
      }
      if (!actif) return
      pageCourante.current = p
      tailleBase.current = { l: base.width * ajuste, h: base.height * ajuste, ajuste }
      const c = fond.current
      c.width = hors.width
      c.height = hors.height
      c.getContext('2d')?.drawImage(hors, 0, 0)
      appliquerTaille(1)
      setRendu(true)
      cadre.current.scrollTo({ top: 0, left: 0 })
    })()
    return () => {
      actif = false
    }
  }, [source, page, appliquerTaille])

  /** Zoom vers un point de l'écran : le point sous le pointeur reste en place. */
  const zoomerVers = useCallback(
    (nouveau: number, px: number, py: number, fx?: number, fy?: number) => {
      const el = feuille.current
      const boite = cadre.current
      if (!el || !boite || !tailleBase.current.l) return
      const z = Math.min(ZOOM_MAX, Math.max(1, nouveau))
      if (Math.abs(z - zoomVisuel.current) < 0.001) return
      const r = el.getBoundingClientRect()
      const ax = fx ?? (px - r.left) / r.width
      const ay = fy ?? (py - r.top) / r.height
      zoomVisuel.current = z
      if (detail.current) detail.current.style.display = 'none'
      appliquerTaille(z)
      const r2 = el.getBoundingClientRect()
      boite.scrollLeft += r2.left + ax * r2.width - px
      boite.scrollTop += r2.top + ay * r2.height - py
      window.clearTimeout(minuterie.current)
      minuterie.current = window.setTimeout(() => setZoom(z), 160)
      planifierDetail(170)
    },
    [appliquerTaille, planifierDetail]
  )

  // Boutons − / + : zoom vers le centre de l'écran.
  const zoomBouton = (facteur: number) => {
    const r = cadre.current?.getBoundingClientRect()
    if (!r) return
    zoomerVers(zoomVisuel.current * facteur, r.left + r.width / 2, r.top + r.height / 2)
  }

  // Molette de la souris : zoom vers le pointeur.
  useEffect(() => {
    const el = cadre.current
    if (!el) return
    function roue(e: WheelEvent) {
      e.preventDefault()
      zoomerVers(zoomVisuel.current * Math.exp(-e.deltaY * 0.0015), e.clientX, e.clientY)
    }
    el.addEventListener('wheel', roue, { passive: false })
    return () => el.removeEventListener('wheel', roue)
  }, [zoomerVers])

  // Pincement à deux doigts (téléphone) : géré par l'application plutôt que
  // par le navigateur, pour que le plan soit redessiné net.
  useEffect(() => {
    const el = cadre.current
    if (!el) return
    const dist = (t: TouchList) =>
      Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY)
    const milieu = (t: TouchList) => ({
      x: (t[0].clientX + t[1].clientX) / 2,
      y: (t[0].clientY + t[1].clientY) / 2
    })
    function debut(e: TouchEvent) {
      if (e.touches.length !== 2 || !feuille.current) return
      e.preventDefault()
      const m = milieu(e.touches)
      const r = feuille.current.getBoundingClientRect()
      pince.current = {
        dist: dist(e.touches),
        z: zoomVisuel.current,
        fx: (m.x - r.left) / r.width,
        fy: (m.y - r.top) / r.height
      }
      toucher.current = null // pas de changement de page pendant un pincement
    }
    function mouvement(e: TouchEvent) {
      const p = pince.current
      if (!p || e.touches.length !== 2) return
      e.preventDefault()
      const m = milieu(e.touches)
      zoomerVers(p.z * (dist(e.touches) / p.dist), m.x, m.y, p.fx, p.fy)
    }
    function fin(e: TouchEvent) {
      if (e.touches.length < 2) pince.current = null
    }
    el.addEventListener('touchstart', debut, { passive: false })
    el.addEventListener('touchmove', mouvement, { passive: false })
    el.addEventListener('touchend', fin)
    el.addEventListener('touchcancel', fin)
    return () => {
      el.removeEventListener('touchstart', debut)
      el.removeEventListener('touchmove', mouvement)
      el.removeEventListener('touchend', fin)
      el.removeEventListener('touchcancel', fin)
    }
  }, [zoomerVers])

  // Fenêtre redimensionnée ou téléphone tourné : on redessine la partie visible.
  useEffect(() => {
    const r = () => planifierDetail(200)
    window.addEventListener('resize', r)
    return () => window.removeEventListener('resize', r)
  }, [planifierDetail])

  // Clavier : ← → et Page préc. / Page suiv., + et − pour le zoom.
  useEffect(() => {
    function touche(e: KeyboardEvent) {
      if ((e.target as HTMLElement)?.tagName === 'INPUT') return
      if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        e.preventDefault()
        suivante()
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault()
        precedente()
      } else if (e.key === 'Escape') {
        index()
      } else if (e.key === '+' || e.key === '=') {
        zoomBouton(1.5)
      } else if (e.key === '-') {
        zoomBouton(1 / 1.5)
      }
    }
    window.addEventListener('keydown', touche)
    return () => window.removeEventListener('keydown', touche)
  })

  // Préchargement des pages voisines pour que le défilement soit instantané.
  useEffect(() => {
    if (page < total) void source.page(page + 1).catch(() => {})
    if (page > 1) void source.page(page - 1).catch(() => {})
  }, [source, page, total])

  const f = feuillets.get(page)
  const zoome = zoom > 1.02

  return (
    <div className="feuille">
      <header className="entete">
        <div className="entete-rang">
          <button className="retour" onClick={index}>
            ‹ Index
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {f?.code || f?.titre ? (
                <>
                  {f.code && <span style={{ fontFamily: 'var(--mono)' }}>{f.code}</span>}
                  {f.code && f.titre ? ' — ' : ''}
                  {f.titre}
                </>
              ) : (
                `Page ${page}`
              )}
            </h1>
            <p className="sous" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              page {page} / {total} · {nomDocument}
            </p>
          </div>
          <button className="fermer-x" onClick={index} aria-label="Fermer la page" title="Fermer (Échap)">
            ✕
          </button>
        </div>
      </header>

      <div
        className="pdf-cadre"
        ref={cadre}
        style={{ cursor: main ? 'grabbing' : 'grab', touchAction: 'pan-x pan-y' }}
        onScroll={() => {
          if (zoomVisuel.current > 1.02) planifierDetail()
        }}
        onPointerDown={(e) => {
          // Souris : on attrape le plan pour le déplacer (la main).
          if (e.pointerType !== 'mouse' || e.button !== 0 || !cadre.current) return
          e.preventDefault()
          ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
          glisse.current = {
            x: e.clientX,
            y: e.clientY,
            sl: cadre.current.scrollLeft,
            st: cadre.current.scrollTop
          }
          setMain(true)
        }}
        onPointerMove={(e) => {
          const g = glisse.current
          if (!g || !cadre.current) return
          cadre.current.scrollLeft = g.sl - (e.clientX - g.x)
          cadre.current.scrollTop = g.st - (e.clientY - g.y)
        }}
        onPointerUp={() => {
          glisse.current = null
          setMain(false)
        }}
        onPointerCancel={() => {
          glisse.current = null
          setMain(false)
        }}
        onTouchStart={(e) => {
          if (e.touches.length !== 1 || zoomVisuel.current > 1.02) return
          toucher.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
        }}
        onTouchEnd={(e) => {
          const debut = toucher.current
          toucher.current = null
          if (!debut || zoomVisuel.current > 1.02) return
          const dx = e.changedTouches[0].clientX - debut.x
          const dy = e.changedTouches[0].clientY - debut.y
          if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
            if (dx < 0) suivante()
            else precedente()
          }
        }}
        onDoubleClick={(e) => {
          // Double-clic : zoom ×2,5 vers le point, ou retour à la page entière.
          if (zoomVisuel.current > 1.02) zoomerVers(1, e.clientX, e.clientY)
          else zoomerVers(2.5, e.clientX, e.clientY)
        }}
      >
        {erreur && (
          <p className="avis erreur" style={{ margin: 16 }}>
            {erreur}
          </p>
        )}
        {!rendu && !erreur && <p className="vide">Affichage de la page…</p>}
        <div ref={feuille} className="pdf-feuille" style={{ display: rendu ? 'block' : 'none' }}>
          <canvas ref={fond} className="pdf-fond" />
          <canvas ref={detail} className="pdf-detail" />
        </div>
      </div>

      <div className="pdf-barre">
        <button
          className="pdf-btn"
          onClick={precedente}
          disabled={page <= 1}
          aria-label="Page précédente"
          title="Page précédente (←)"
        >
          ‹
        </button>
        <input
          className="pdf-page"
          type="text"
          inputMode="numeric"
          value={saisie}
          onChange={(e) => setSaisie(e.target.value.replace(/\D/g, ''))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
          }}
          onBlur={() => {
            const n = parseInt(saisie, 10)
            if (!isNaN(n) && n >= 1 && n <= total) allerA(n)
            else setSaisie(String(page))
          }}
          aria-label="Numéro de page"
        />
        <button
          className="pdf-btn"
          onClick={suivante}
          disabled={page >= total}
          aria-label="Page suivante"
          title="Page suivante (→)"
        >
          ›
        </button>
        <span style={{ flex: 1 }} />
        <button className="pdf-btn" onClick={() => zoomBouton(1 / 1.5)} disabled={!zoome} aria-label="Réduire">
          −
        </button>
        <span className="code" style={{ minWidth: 44, textAlign: 'center' }}>
          {Math.round(zoom * 100)} %
        </span>
        <button
          className="pdf-btn"
          onClick={() => zoomBouton(1.5)}
          disabled={zoom >= ZOOM_MAX}
          aria-label="Agrandir"
        >
          +
        </button>
      </div>
    </div>
  )
}
